import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@common/components/Box';
import IconButton from '@common/components/controls/IconButton';
import Icon from '@common/components/Icon';
import IconClose from '@common/components/svgs/common/Close';
import IconInfo from '@common/components/svgs/common/Info';
import classnames from '@common/utils/ui/classnames';
import '@common/styles/components/bluebox.pcss';

export interface BlueboxProps {
  text?: string;
  onClose?: () => void;
}

const STICKY_TOP_OFFSET_VAR = '--sticky-top-offset';

function Bluebox({ text, onClose }: Readonly<BlueboxProps>) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState<boolean>(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const onCloseClicked = () => {
    setEnabled(false);
    if (onClose) {
      setTimeout(onClose, 300);
    }
  };

  useEffect(() => {
    setEnabled(!!text);
  }, [text]);

  // Track the Bluebox's rendered height in a CSS variable so sticky elements
  // (e.g. table headers) can offset below it. Runs while the Bluebox is mounted
  // (including during the slide-up close animation) so the offset stays correct
  // until the banner is fully gone. useLayoutEffect (not useEffect) so the
  // measurement lands before the first paint and the CSS `:has()` 64px fallback
  // doesn't briefly show on a multi-line Bluebox.
  useLayoutEffect(() => {
    if (!text) {
      return undefined;
    }
    const element = rootRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const writeOffset = () => {
      document.documentElement.style.setProperty(
        STICKY_TOP_OFFSET_VAR,
        `${element.offsetHeight}px`,
      );
    };
    writeOffset();
    const observer = new ResizeObserver(writeOffset);
    observer.observe(element);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(STICKY_TOP_OFFSET_VAR);
    };
  }, [text]);

  if (!text) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      className={classnames({
        bluebox: true,
        'bluebox--closed': !enabled,
      })}
    >
      <div className="container justify-content-space-between">
        <div className="align-items-center">
          <Box mr={4} className="align-items-center">
            <Icon component={IconInfo} />
          </Box>
          <span className="bluebox__text">
            {text}
          </span>
        </div>
        <Box mr={2} className="align-items-center">
          <IconButton
            icon={IconClose}
            darkBackground
            large
            onClick={onCloseClicked}
            data-testid="blueboxCloseButton"
            title={t('common.titles.bluebox.close')}
          />
        </Box>
      </div>
    </div>
  );
}

export default Bluebox;
